import React, {
  useState,
  useEffect,
  useRef,
  memo,
  useCallback,
  useMemo,
} from "react";
import { Container } from "../HtmlTags/Container/Container";
import { RawButton } from "../HtmlTags/Button/RawButton";
import { ChevronLeft, ChevronRight } from "lucide-react";
import "./ImageSlider.css";

interface ImageSliderProps {
  images: string[];
  slideChangeDuration?: number;
  wrapperClass?: string;
  enableNavigationArrows?: boolean;
  enableNavigationDots?: boolean;
  enableSliderCounter?: boolean;
  imageTransitionSpeed?: number;
}

const ImageSlider = memo<ImageSliderProps>(
  ({
    images,
    slideChangeDuration = 4000,
    wrapperClass,
    enableNavigationArrows = false,
    enableNavigationDots = false,
    enableSliderCounter = false,
    imageTransitionSpeed = 0.75,
  }) => {
    const [currentIndex, setCurrentIndex] = useState<number>(1);
    const [isTransitioning, setIsTransitioning] = useState<boolean>(true);
    const [forceUpdate, setForceUpdate] = useState<number>(0);
    const sliderRef = useRef<HTMLDivElement>(null);
    const wrapperRef = useRef<HTMLDivElement>(null);

    const extendedImages = useMemo(() => {
      if (images.length === 0) return [];
      return [images[images.length - 1], ...images, images[0]];
    }, [images]);

    const displayIndex = useMemo(() => {
      if (currentIndex === 0) return images.length - 1;
      if (currentIndex === images.length + 1) return 0;
      return currentIndex - 1;
    }, [currentIndex, images.length]);

    const sliderStyle = useMemo(
      () => ({
        transform: `translateX(-${currentIndex * 100}%)`,
        transition: isTransitioning
          ? `transform ${imageTransitionSpeed}s ease-in-out`
          : "none",
      }),
      // eslint-disable-next-line
      [currentIndex, isTransitioning, imageTransitionSpeed, forceUpdate]
    );

    const goToSlide = useCallback((index: number): void => {
      setIsTransitioning(true);
      setCurrentIndex(index + 1);
    }, []);

    const goToPrevious = useCallback((): void => {
      setIsTransitioning(true);
      setCurrentIndex((prevIndex) => {
        if (prevIndex <= 0) return prevIndex;
        return prevIndex - 1;
      });
    }, []);

    const goToNext = useCallback((): void => {
      setIsTransitioning(true);
      setCurrentIndex((prevIndex) => {
        if (prevIndex >= images.length + 1) return prevIndex;
        return prevIndex + 1;
      });
    }, [images.length]);

    useEffect(() => {
      const handleResize = () => {
        setForceUpdate((prev) => prev + 1);
      };

      const handleVisibilityChange = () => {
        if (document.hidden) return;
        setForceUpdate((prev) => prev + 1);
        setCurrentIndex((prevIdx) => {
          const snapped =
            prevIdx === 0
              ? images.length
              : prevIdx === images.length + 1
                ? 1
                : prevIdx;
          if (snapped !== prevIdx) {
            queueMicrotask(() => setIsTransitioning(false));
          }
          return snapped;
        });
      };
      let resizeObserver: ResizeObserver | null = null;
      if (wrapperRef.current) {
        resizeObserver = new ResizeObserver(handleResize);
        resizeObserver.observe(wrapperRef.current);
      }
      window.addEventListener("resize", handleResize);
      document.addEventListener("visibilitychange", handleVisibilityChange);

      return () => {
        if (resizeObserver) {
          resizeObserver.disconnect();
        }
        window.removeEventListener("resize", handleResize);
        document.removeEventListener(
          "visibilitychange",
          handleVisibilityChange
        );
      };
    }, [images.length]);

    useEffect(() => {
      if (images.length <= 1) return;

      const interval = setInterval(() => {
        if (document.hidden) return;
        goToNext();
      }, slideChangeDuration);

      return () => clearInterval(interval);
    }, [images.length, slideChangeDuration, goToNext]);

    useEffect(() => {
      const handleTransitionEnd = () => {
        if (currentIndex === 0) {
          setIsTransitioning(false);
          setCurrentIndex(images.length);
        } else if (currentIndex === images.length + 1) {
          setIsTransitioning(false);
          setCurrentIndex(1);
        }
      };

      const slider = sliderRef.current;
      if (slider) {
        slider.addEventListener("transitionend", handleTransitionEnd);
        return () =>
          slider.removeEventListener("transitionend", handleTransitionEnd);
      }
    }, [currentIndex, images.length]);

    useEffect(() => {
      if (!isTransitioning) {
        const timer = setTimeout(() => {
          setIsTransitioning(true);
        }, 50);
        return () => clearTimeout(timer);
      }
    }, [isTransitioning]);

    if (images.length === 0) {
      return null;
    }

    return (
      <Container
        ref={wrapperRef}
        className={`image-slider-wrap ${wrapperClass ? wrapperClass : ""}`}
        data-testid="image-slider"
      >
        <Container
          ref={sliderRef}
          className="image-slider-inner-cont"
          style={sliderStyle}
        >
          {extendedImages.map((image, index) => (
            <Container key={`${image}-${index}`} className="slider-img-wrap">
              <img src={image} alt={`Slide ${index}`} />
            </Container>
          ))}
        </Container>

        {enableNavigationArrows && images.length > 1 && (
          <>
            <RawButton
              onClick={goToPrevious}
              className="image-slider-nav-btn nav-prev"
              aria-label="Previous image"
            >
              <ChevronLeft className="image-slider-nav-icon" />
            </RawButton>
            <RawButton
              onClick={goToNext}
              className="image-slider-nav-btn nav-next"
              aria-label="Next image"
            >
              <ChevronRight className="image-slider-nav-icon" />
            </RawButton>
          </>
        )}

        {enableNavigationDots && images.length > 1 && (
          <Container className="image-slider-nav-dot-wrap">
            {images.map((_, index) => (
              <RawButton
                key={index}
                onClick={() => goToSlide(index)}
                className={`${index === displayIndex ? "slide-active" : ""}`}
                aria-label={`Go to slide ${index + 1}`}
              >
                {index}
              </RawButton>
            ))}
          </Container>
        )}

        {enableSliderCounter && images.length > 1 && (
          <Container className="image-slider-counter">
            {displayIndex + 1} / {images.length}
          </Container>
        )}
      </Container>
    );
  }
);

ImageSlider.displayName = "ImageSlider";

export default ImageSlider;

/**
 * A responsive image slider component with infinite loop functionality.
 *
 * Features:
 * - Automatic slide transitions with configurable timing
 * - Infinite loop navigation (seamlessly loops from last to first image)
 * - Optional navigation arrows for manual control
 * - Optional navigation dots for direct slide access
 * - Optional slide counter display
 * - Smooth CSS transitions with configurable speed
 * - Responsive design
 * - Accessibility support with ARIA labels
 *
 * @example
 * ```tsx
 * // Basic usage
 * <ImageSlider images={['image1.jpg', 'image2.jpg', 'image3.jpg']} />
 *
 * // With all features enabled
 * <ImageSlider
 *   images={['image1.jpg', 'image2.jpg', 'image3.jpg']}
 *   slideChangeDuration={3000}
 *   enableNavigationArrows={true}
 *   enableNavigationDots={true}
 *   enableSliderCounter={true}
 *   imageTransitionSpeed={0.5}
 *   wrapperClass="custom-slider"
 * />
 * ```
 *
 * @param props - The component props
 * @param props.images - Array of image URLs to display in the slider
 * @param props.slideChangeDuration - Duration in milliseconds between automatic slide transitions (default: 4000)
 * @param props.wrapperClass - Additional CSS class name for the slider wrapper
 * @param props.enableNavigationArrows - Whether to show navigation arrow buttons (default: false)
 * @param props.enableNavigationDots - Whether to show navigation dot indicators (default: false)
 * @param props.enableSliderCounter - Whether to show the slide counter (default: false)
 * @param props.imageTransitionSpeed - Duration in seconds for slide transition animation (default: 0.75)
 *
 * @returns A React component that renders an image slider, or null if no images are provided
 *
 * @since 1.0.0
 * @author Your Name
 */
